import { useState, useEffect, useRef } from "react";
import { supabase, hasLocalStorage } from "../lib/supabase";
import { Lock, Save, Eye, EyeOff, AlertTriangle, ExternalLink } from "lucide-react";
import PasswordStrength, { passwordIsStrong } from "../components/shared/PasswordStrength";
import FormField from "../components/forms/FormField";
import BrandLogo from "../components/shared/BrandLogo";
import { toast } from "@/lib/toast";
import { useNavigate } from "react-router-dom";
import { useIsMounted } from "../hooks/useIsMounted";

// Detect iOS WebViews used by Gmail, Outlook, LinkedIn, etc.
// These open links in an in-app browser that has localStorage but may be isolated
// from the user's main Safari session. The recovery itself works, but the resulting
// session won't carry over to the home-screen PWA or Safari.
// Heuristic: iOS + AppleWebKit present + no "Safari/<version>" suffix in UA.
function isIsolatedWebView() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return (
    /iPhone|iPad|iPod/.test(ua) &&
    /AppleWebKit/.test(ua) &&
    !/Safari\//.test(ua)
  );
}

export default function SetPassword() {
  const navigate    = useNavigate();
  const isMounted   = useIsMounted();
  const pwdRef      = useRef(null);

  const [password,      setPassword]      = useState("");
  const [confirm,       setConfirm]       = useState("");
  const [showPwd,       setShowPwd]       = useState(false);
  const [showConfirm,   setShowConfirm]   = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [ready,         setReady]         = useState(false);
  const [expired,       setExpired]       = useState(false);
  const [expiredReason, setExpiredReason] = useState("expired"); // "expired" | "private" | "webview"
  const [flowType,      setFlowType]      = useState("invite");  // "invite" | "recovery"

  useEffect(() => {
    const init = async () => {
      // ── 1. Determine flow type ────────────────────────────────────────────
      const params      = new URLSearchParams(window.location.search);
      const typeParam   = params.get("type");
      const hash        = window.location.hash;
      const hashType    = hash.includes("type=recovery") ? "recovery" : "invite";
      const resolvedType =
        typeParam === "recovery" || hashType === "recovery" ? "recovery" : "invite";
      setFlowType(resolvedType);

      // ── 2. Fallback token exchange ────────────────────────────────────────
      // In the normal App.js flow, PASSWORD_RECOVERY / SIGNED_IN redirect to
      // /set-password?type=... with no hash (session already in storage).
      // This branch handles the edge case where the user arrives directly at
      // /set-password#access_token=... (e.g., a stale bookmark or server-side
      // redirect that skipped the App.js listener).
      if (hash && hash.includes("access_token")) {
        const hashParams    = new URLSearchParams(hash.replace("#", "?"));
        const access_token  = hashParams.get("access_token");
        const refresh_token = hashParams.get("refresh_token");
        if (access_token && refresh_token) {
          await supabase.auth.setSession({ access_token, refresh_token });
          window.history.replaceState(null, "", `/set-password?type=${resolvedType}`);
        }
      }

      // ── 3. Verify session ─────────────────────────────────────────────────
      const { data } = await supabase.auth.getSession();
      if (!isMounted()) return;

      if (data.session) {
        setReady(true);
        setTimeout(() => pwdRef.current?.focus(), 100);
        return;
      }

      // ── 4. Diagnose why there's no session ───────────────────────────────
      // Shown in the expired state for better user guidance.
      if (!navigator.onLine) {
        // No network — token exchange never completed. The link may still be valid.
        setExpiredReason("offline");
      } else if (!hasLocalStorage) {
        // Safari Private Browsing (or other no-storage context):
        // Supabase used in-memory storage. After the redirect from App.js,
        // the page reload wiped the in-memory session.
        setExpiredReason("private");
      } else if (isIsolatedWebView()) {
        // iOS in-app browser (Gmail, Outlook, etc.):
        // Session was stored in isolated storage. Recovery works, but the
        // resulting session won't carry over to the main Safari/PWA session.
        // We show a hint to open in Safari for a better experience.
        setExpiredReason("webview");
      } else {
        setExpiredReason("expired");
      }

      setExpired(true);
    };

    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (loading) return;

    if (!password || password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      pwdRef.current?.focus();
      return;
    }
    if (!passwordIsStrong(password)) {
      toast.error("La contraseña no cumple los requisitos de seguridad");
      pwdRef.current?.focus();
      return;
    }
    if (password !== confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      toast.success(
        flowType === "recovery"
          ? "Contraseña actualizada"
          : "Contraseña creada — bienvenido"
      );

      if (flowType === "recovery") {
        // Detect user type BEFORE signing out (session is still active here).
        let destination = "/login?view=pastor";
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const { data: profile } = await supabase
              .from("profiles")
              .select("id")
              .eq("id", user.id)
              .maybeSingle();
            if (!profile) destination = "/member/access?view=login";
          }
        } catch {
          // Query failed — default to pastor login
        }
        await supabase.auth.signOut();
        window.location.href = destination;
      } else {
        // Invite flow: "/" is correct — RequireDistrict redirects to /create-district.
        window.location.href = "/";
      }
    } catch (err) {
      const msg =
        err?.message?.includes("same password")
          ? "La nueva contraseña debe ser diferente a la anterior"
          : "No se pudo guardar la contraseña. Intenta de nuevo.";
      toast.error(msg);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
  };

  // ── Expired / no session ────────────────────────────────────────────────────
  if (expired) {
    const isOffline = expiredReason === "offline";
    const isPrivate = expiredReason === "private";
    const isWebView = expiredReason === "webview";
    const isWarning = isOffline || isPrivate || isWebView;

    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#f4f8fc] dark:bg-[#050d1e] px-4 pb-[env(safe-area-inset-bottom)]">
        <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-[28px] p-6 border border-slate-100 dark:border-slate-800 shadow-[0_4px_24px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.4)] space-y-5 text-center">

          <BrandLogo variant="auto" className="w-16 h-16 mx-auto object-contain opacity-60" />

          {/* Icon badge */}
          <div className="flex justify-center">
            <div className={`h-11 w-11 rounded-[14px] flex items-center justify-center ${
              isWarning
                ? "bg-amber-50 dark:bg-amber-900/20"
                : "bg-red-50 dark:bg-red-900/20"
            }`}>
              {isWarning
                ? <AlertTriangle size={18} className="text-amber-500" />
                : <Lock size={18} className="text-red-400 dark:text-red-500" />
              }
            </div>
          </div>

          <div className="space-y-1.5">
            <h2 className="text-[16px] font-semibold text-slate-800 dark:text-slate-100">
              {isOffline
                ? "Sin conexión a internet"
                : isPrivate
                ? "Modo privado detectado"
                : isWebView
                ? "Abre el enlace en Safari"
                : "El enlace expiró"}
            </h2>
            <p className="text-[12px] text-slate-500 dark:text-slate-400 leading-relaxed">
              {isOffline
                ? "El enlace no pudo verificarse sin conexión. Reconéctate y vuelve a abrirlo desde tu correo."
                : isPrivate
                ? "Los enlaces de recuperación no funcionan en modo privado porque el navegador bloquea el almacenamiento de sesiones. Ábrelo en una ventana normal."
                : isWebView
                ? "Estás usando el navegador integrado de una app (Gmail, Outlook, etc.). Para una mejor experiencia, copia el enlace y ábrelo en Safari."
                : flowType === "recovery"
                ? "Los enlaces de recuperación son válidos por 1 hora. Solicita uno nuevo si ya expiró."
                : "Los enlaces de invitación son válidos por 24 horas. Pide al administrador que reenvíe tu invitación."}
            </p>
          </div>

          {/* Context-specific hint */}
          {(isPrivate || isWebView || isOffline) && (
            <div className="bg-slate-50 dark:bg-slate-800/60 rounded-[14px] px-4 py-3 text-[11px] text-slate-500 dark:text-slate-400 flex items-start gap-2 text-left">
              <ExternalLink size={13} className="shrink-0 mt-0.5 text-slate-400" />
              <span>
                {isOffline
                  ? "El enlace sigue siendo válido. Una vez con conexión, ábrelo directamente desde tu correo."
                  : isPrivate
                  ? "Cierra la ventana privada, abre la app en modo normal y vuelve a usar el enlace del correo."
                  : "Mantén presionado el enlace en tu correo y elige \"Abrir en Safari\"."}
              </span>
            </div>
          )}

          {/* Primary action */}
          {isOffline ? (
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-brand text-white py-3 rounded-[14px] text-[15px] font-semibold transition-all duration-200 hover:brightness-90 active:scale-[0.98]"
            >
              Reintentar
            </button>
          ) : flowType === "recovery" ? (
            <button
              onClick={() => navigate("/forgot-password")}
              className="w-full bg-brand text-white py-3 rounded-[14px] text-[15px] font-semibold transition-all duration-200 hover:brightness-90 active:scale-[0.98]"
            >
              Solicitar nuevo enlace
            </button>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-full bg-brand text-white py-3 rounded-[14px] text-[15px] font-semibold transition-all duration-200 hover:brightness-90 active:scale-[0.98]"
            >
              Volver al inicio
            </button>
          )}

          {/* Secondary escape */}
          <button
            onClick={() => navigate("/login")}
            className="w-full text-[13px] text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors py-1"
          >
            Ir al inicio de sesión
          </button>

        </div>
      </div>
    );
  }

  // ── Resolving session ───────────────────────────────────────────────────────
  if (!ready) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#f4f8fc] dark:bg-[#050d1e]">
        <p className="text-[13px] text-slate-400 dark:text-slate-500 animate-pulse">
          Preparando acceso…
        </p>
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────────
  const isRecovery = flowType === "recovery";
  const matchError = confirm.length > 0 && password !== confirm;
  const canSubmit  = password.length >= 8 && confirm.length >= 8 && !matchError && passwordIsStrong(password);

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-[#f4f8fc] dark:bg-[#050d1e] px-4 pb-[calc(env(safe-area-inset-bottom)+24px)]">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-[28px] p-6 border border-slate-100 dark:border-slate-800 shadow-[0_4px_24px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.4)] space-y-5">

        {/* Wordmark */}
        <div className="flex flex-col items-center">
          <BrandLogo variant="auto" className="w-14 h-14 object-contain animate-logo" />
          <h1 className="mt-2 text-[20px] font-bold text-brand dark:text-blue-400">Kéves App</h1>
          <p className="text-[12px] text-slate-400 dark:text-slate-500 mt-0.5">
            {isRecovery ? "Recuperar acceso" : "Configurar acceso"}
          </p>
        </div>

        <div className="text-center">
          <h2 className="text-[15px] font-semibold text-slate-800 dark:text-slate-100">
            {isRecovery ? "Nueva contraseña" : "Crear contraseña"}
          </h2>
          <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5">
            {isRecovery
              ? "Elige una contraseña segura para tu cuenta"
              : "Establece tu contraseña para acceder a la app"}
          </p>
        </div>

        {/* WebView hint for in-app browsers that DO have localStorage */}
        {isIsolatedWebView() && (
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-[14px] px-4 py-3 text-[11px] text-amber-700 dark:text-amber-400 flex items-start gap-2">
            <AlertTriangle size={13} className="shrink-0 mt-0.5" />
            <span>
              Estás usando un navegador integrado. La sesión no se guardará en tu app principal. Para mejor experiencia, abre el enlace en Safari.
            </span>
          </div>
        )}

        {/* Password */}
        <div className="space-y-1.5">
          <label
            htmlFor="new-password"
            className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 dark:text-slate-400"
          >
            <Lock size={11} />
            {isRecovery ? "Nueva contraseña" : "Contraseña"}
          </label>
          <FormField
            ref={pwdRef}
            id="new-password"
            type={showPwd ? "text" : "password"}
            name="new-password"
            autoComplete="new-password"
            placeholder="Mínimo 8 caracteres"
            value={password}
            onChange={setPassword}
            onKeyDown={handleKeyDown}
            rightSlot={
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPwd((v) => !v)}
                className="h-11 w-11 -mr-1.5 flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            }
          />
          <PasswordStrength password={password} />
        </div>

        {/* Confirm password */}
        <div className="space-y-1.5">
          <label
            htmlFor="confirm-password"
            className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 dark:text-slate-400"
          >
            <Lock size={11} />
            Confirmar contraseña
          </label>
          <FormField
            id="confirm-password"
            type={showConfirm ? "text" : "password"}
            name="confirm-password"
            autoComplete="new-password"
            placeholder="Repite la contraseña"
            value={confirm}
            onChange={setConfirm}
            onKeyDown={handleKeyDown}
            error={matchError ? "Las contraseñas no coinciden" : undefined}
            rightSlot={
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowConfirm((v) => !v)}
                className="h-11 w-11 -mr-1.5 flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                aria-label={showConfirm ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            }
          />
        </div>

        {/* Submit */}
        <button
          onClick={handleSave}
          disabled={loading || !canSubmit}
          className="w-full h-12 bg-brand text-white rounded-[14px] text-[15px] font-semibold shadow-sm transition-all duration-200 ease-out hover:brightness-105 active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
              Guardando…
            </>
          ) : (
            <>
              <Save size={16} />
              {isRecovery ? "Actualizar contraseña" : "Crear contraseña"}
            </>
          )}
        </button>

      </div>
    </div>
  );
}
